/**
 * Sessions API Service
 * Handles therapy session management and data collection
 */

import { get, post, put, del } from './client';

// =============================================================================
// Session Endpoints
// =============================================================================

/**
 * List sessions with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.client_id - Filter by client
 * @param {string} params.therapist_id - Filter by therapist
 * @param {string} params.date - Filter by date
 * @param {string} params.start_date - Start of date range
 * @param {string} params.end_date - End of date range
 * @param {string} params.status - Filter by status
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of sessions
 */
export const listSessions = (params = {}) => get('/sessions', params);

/**
 * Get session by ID with full details
 * @param {string} sessionId - Session UUID
 * @returns {Promise} Session data with data entries
 */
export const getSession = (sessionId) => get(`/sessions/${sessionId}`);

/**
 * Create new session
 * @param {Object} data - Session data
 * @param {string} data.client_id - Client ID
 * @param {string} data.therapist_id - Therapist ID
 * @param {string} data.scheduled_date - Scheduled date
 * @param {string} data.scheduled_start_time - Start time
 * @param {string} data.scheduled_end_time - End time
 * @param {string} data.session_type - Session type
 * @returns {Promise} Created session
 */
export const createSession = (data) => post('/sessions', data);

/**
 * Update session, optionally spanning its recurring series.
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Session data to update
 * @param {Object} [opts]
 * @param {'single'|'following'|'series'} [opts.scope='single'] - 'single' edits
 *   only this occurrence (its date included); 'following' also rewrites every
 *   later occurrence in the series; 'series' rewrites all of them. For the two
 *   series scopes the backend keeps each occurrence's own date and carries a
 *   time change across as time-of-day + duration, skipping occurrences that
 *   are past, started, or already have data collected.
 * @returns {Promise} Updated session (with series_updated_count)
 */
export const updateSession = (sessionId, data, opts = {}) =>
  put(`/sessions/${sessionId}`, data, opts.scope && opts.scope !== 'single' ? { scope: opts.scope } : {});

/**
 * Delete session.
 * @param {string} sessionId - Session UUID
 * @param {Object} [opts]
 * @param {'single'|'series'} [opts.scope='single'] - 'single' deletes just
 *   this session; 'series' also deletes every other future fresh session
 *   sharing the same scheduling template.
 * @returns {Promise} Delete response { success, message, id }
 */
export const deleteSession = async (sessionId, { scope = 'single' } = {}) => {
  const result = await del(`/sessions/${sessionId}`, { scope });
  window.dispatchEvent(new Event('ayro:data-updated'));
  return result;
};

export const bulkDeleteSessions = (sessionIds) => post('/sessions/bulk-delete', { session_ids: sessionIds });

// =============================================================================
// Session Status Management
// =============================================================================

/**
 * Start a session
 * @param {string} sessionId - Session UUID
 * @returns {Promise} Updated session
 */
export const startSession = (sessionId) => post(`/sessions/${sessionId}/start`);

/**
 * End a session (record actual end time and calculate duration)
 * @param {string} sessionId - Session UUID
 * @param {Object} data - End data
 * @param {string} data.actual_end - Actual end time
 * @param {string} data.status_id - Completed status UUID
 * @returns {Promise} Updated session
 */
export const completeSession = (sessionId, data = {}) => post(`/sessions/${sessionId}/end`, data);

/**
 * Cancel a session
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Cancellation data
 * @param {string} data.cancellation_reason - Reason for cancellation
 * @returns {Promise} Cancelled session
 */
export const cancelSession = (sessionId, data) => post(`/sessions/${sessionId}/cancel`, data);

/**
 * Mark session as no-show
 * @param {string} sessionId - Session UUID
 * @param {Object} [data] - Optional data with cancellation_reason
 * @returns {Promise} Updated session
 */
export const markNoShow = (sessionId, data) => post(`/sessions/${sessionId}/no-show`, data);

// =============================================================================
// Reference Data
// =============================================================================

/**
 * Get reference data for session creation (services, session types, statuses)
 * @returns {Promise} { services, session_types, statuses }
 */
export const getSessionReferenceData = () => get('/sessions/reference-data');

// =============================================================================
// Scheduling Templates & Instances
// =============================================================================

/**
 * List scheduling templates
 * @param {Object} params - Query parameters
 * @returns {Promise} List of templates
 */
export const listSchedulingTemplates = (params = {}) => get('/scheduling/templates', params);

/**
 * Create a scheduling template (recurring session pattern)
 * @param {Object} data - Template data
 * @returns {Promise} Created template
 */
export const createSchedulingTemplate = (data) => post('/scheduling/templates', data);

/**
 * Create a scheduling instance (single session occurrence)
 * @param {Object} data - Instance data
 * @returns {Promise} Created instance
 */
export const createSchedulingInstance = (data) => post('/scheduling/instances', data);

// =============================================================================
// Therapist Session Queries
// =============================================================================

/**
 * Get today's sessions for a therapist
 * @param {string} therapistId - Therapist UUID
 * @param {string} date - Date (defaults to today)
 * @returns {Promise} List of sessions
 */
export const getTherapistDailySessions = (therapistId, date = null) => {
  const params = { therapist_id: therapistId };
  if (date) {
    params.session_date = date;
  }
  return get('/sessions/today', params);
};

/**
 * Get therapist's session calendar
 * @param {string} therapistId - Therapist UUID
 * @param {string} startDate - Start of period
 * @param {string} endDate - End of period
 * @returns {Promise} List of sessions for calendar
 */
export const getTherapistCalendar = (therapistId, startDate, endDate) =>
  get('/sessions/calendar', {
    therapist_id: therapistId,
    start_date: startDate,
    end_date: endDate
  });

/**
 * Get sessions pending data entry
 * @param {string} therapistId - Therapist UUID
 * @returns {Promise} List of sessions needing data
 */
export const getPendingDataEntry = (therapistId) => get('/sessions/pending-data', { therapist_id: therapistId });

// =============================================================================
// Session Data Collection
// =============================================================================

/**
 * Get data collections for a session
 * Uses the data-collection endpoint with session_id filter
 * @param {string} sessionId - Session UUID
 * @returns {Promise} Paginated list of data collections
 */
export const getSessionData = (sessionId) => get('/data-collection', { session_id: sessionId });

/**
 * Every change ever made to one collected datasheet entry, newest first.
 *
 * Served from audit.data_collection, which a database trigger fills on every
 * INSERT/UPDATE/DELETE - so it covers rows changed long before the amendment rule
 * existed, and it cannot be bypassed by a write that skips the API.
 *
 * Readable by anyone who may read the entry, INCLUDING the therapist who collected
 * it: being told that a supervisor corrected your figures, and why, is the point of
 * keeping the record.
 *
 * @param {string} collectionId - data collection UUID
 * @returns {Promise<{collection_id: string, amend_count: number, can_amend: boolean, entries: Array}>}
 */
export const getDataCollectionHistory = (collectionId) => get(`/data-collection/${collectionId}/history`);

/**
 * Who else has recorded data against these sessions, excluding the caller.
 *
 * A supervisor and a therapist can both run the same session now, and a
 * therapist works offline - so an entry can sit in the queue for hours while
 * someone else records against the same session on the web.
 *
 * This WARNS rather than blocks. Multiple rows per (session, datasheet) are
 * normal - one per target or trial block - so refusing the second author would
 * break ordinary use. The sync page shows this before pushing anything.
 *
 * @param {string[]} sessionIds up to 200
 * @returns {Promise<{sessions: Record<string, Array<{name, datasheet, entries, last_at}>>}>}
 */
export const getSessionCollectors = (sessionIds = []) => get('/data-collection/session-collectors', { session_ids: sessionIds.join(',') });

/**
 * Get the distinct therapists who have conducted at least one session for a client.
 * Maps to GET /api/v1/sessions/therapists?client_id={clientId}
 * @param {string} clientId - Client UUID
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export const getClientTherapists = (clientId) => get('/sessions/therapists', { client_id: clientId });

/**
 * Get the supervisors currently allocated to a child (active allocations).
 * Maps to GET /api/v1/sessions/supervisors?client_id={clientId}
 * @param {string} clientId - Client UUID
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export const getClientSupervisors = (clientId) => get('/sessions/supervisors', { client_id: clientId });

/**
 * Get data collections for a client — supports datasheet_type_code filter.
 * Use datasheet_type_code='injury_report' to count injury/incident reports.
 * Maps to GET /api/v1/data-collection/client/{clientId}
 * @param {string} clientId - Client UUID
 * @param {Object} params   - { datasheet_type_code, start_date, end_date, page_size, ... }
 * @returns {Promise} Paginated list of data collections
 */
export const listClientDataCollections = (clientId, params = {}) => get(`/data-collection/client/${clientId}`, params);

/**
 * Trial-level progress data for the Goal/Target Progress Over Time chart.
 * Returns (session_date, target_id, correct_trials, total_trials) aggregated
 * per day per target, filtered to cold_probe and multi_trial types only.
 * Maps to GET /api/v1/data-collection/client/{clientId}/goal-target-progress
 * @param {string} clientId - Client UUID
 * @param {Object} params   - { iep_id, start_date, end_date }
 * @returns {Promise} { data_points: [{ session_date, target_id, correct_trials, total_trials }] }
 */
export const getChildGoalTargetProgress = (clientId, params = {}) =>
  get(`/data-collection/client/${clientId}/goal-target-progress`, params);

/**
 * Aggregate operand performance data (prompted, independent, RPM) per session
 * date for the child Operand Progress Over Time chart.
 * Maps to GET /api/v1/data-collection/client/{clientId}/operand-progress
 * @param {string} clientId - Client UUID
 * @param {Object} params   - { iep_id, domain_code, goal_id, target_id, start_date, end_date }
 * @returns {Promise} { data_points: [{ date, prompted, independent, rpm }] }
 */
export const getClientOperandProgress = (clientId, params = {}) => get(`/data-collection/client/${clientId}/operand-progress`, params);

/**
 * Per-date counts grouped by controlling prompt level (PVP/PPP/FPP/PGP/IND).
 * Multiple sessions on the same date are aggregated into one row.
 * @param {string} clientId - Client UUID
 * @param {Object} params   - { iep_id, domain_code, goal_id, target_id, start_date, end_date }
 * @returns {Promise} { data_points: [{ date, PVP, PPP, FPP, PGP, IND }] }
 */
export const getOperandPromptBreakdown = (clientId, params = {}) =>
  get(`/data-collection/client/${clientId}/operand-prompt-breakdown`, params);

/**
 * Get a specific data collection with full details
 * @param {string} collectionId - DataCollection UUID
 * @returns {Promise} Data collection with child records
 */
export const getDataCollection = (collectionId) => get(`/data-collection/${collectionId}`);

/**
 * Create a data collection record
 * @param {Object} data - DataCollection data
 * @returns {Promise} Created data collection
 */
export const createDataCollection = (data) => post('/data-collection', data);

/**
 * Add trial records to a data collection
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} trialData - Trial data
 * @returns {Promise} Created trial
 */
export const addTrial = (collectionId, trialData) => post(`/data-collection/${collectionId}/trials`, trialData);

/**
 * Add behavior record to a data collection
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} behaviorData - Behavior data
 * @returns {Promise} Created behavior record
 */
export const addBehavior = (collectionId, behaviorData) => post(`/data-collection/${collectionId}/behaviors`, behaviorData);

/**
 * Add ABC record to a data collection
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} abcData - ABC data
 * @returns {Promise} Created ABC record
 */
export const addAbc = (collectionId, abcData) => post(`/data-collection/${collectionId}/abc`, abcData);

/**
 * Add mand record to a data collection
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} mandData - Mand data
 * @returns {Promise} Created mand record
 */
export const addMand = (collectionId, mandData) => post(`/data-collection/${collectionId}/mands`, mandData);

/**
 * Add maintenance probe to a data collection
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} probeData - Maintenance probe data
 * @returns {Promise} Created maintenance probe
 */
export const addMaintenance = (collectionId, probeData) => post(`/data-collection/${collectionId}/maintenance`, probeData);

/**
 * Update a data collection record
 * @param {string} collectionId - DataCollection UUID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated data collection
 */
export const updateDataCollection = (collectionId, data) => put(`/data-collection/${collectionId}`, data);

/**
 * Delete a data collection record
 * @param {string} collectionId - DataCollection UUID
 * @returns {Promise} Delete response
 */
export const deleteDataCollection = async (collectionId) => {
  const result = await del(`/data-collection/${collectionId}`);
  window.dispatchEvent(new Event('ayro:data-updated'));
  return result;
};

/**
 * Discard the caller's OWN in-progress collected data for a session — the server half of
 * the "reloading the collection page starts the session over" behaviour. Soft-deletes only
 * the rows this user collected, and only while the session is still open; enforced
 * server-side (see backend discard_own_session_data). Needs only datacollection.update, so
 * a therapist can clear their own pre-refresh rows that the generic DELETE would 403.
 * @param {string} sessionId - Session UUID
 * @returns {Promise<{success: boolean, message: string}>} message carries the deleted count
 */
export const discardOwnSessionData = async (sessionId) => {
  const result = await post(`/data-collection/session/${sessionId}/discard-own`);
  window.dispatchEvent(new Event('ayro:data-updated'));
  return result;
};

// =============================================================================
// Session Notes
// =============================================================================

/**
 * Get session notes
 * @param {string} sessionId - Session UUID
 * @returns {Promise} Session notes
 */
export const getSessionNotes = (sessionId) => get(`/sessions/${sessionId}/notes`);

/**
 * Add session note
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Note data
 * @param {string} data.note_type - Type of note
 * @param {string} data.content - Note content
 * @returns {Promise} Created note
 */
export const addSessionNote = (sessionId, data) => post(`/sessions/${sessionId}/notes`, data);

/**
 * Update session note
 * @param {string} sessionId - Session UUID
 * @param {string} noteId - Note UUID
 * @param {Object} data - Updated note data
 * @returns {Promise} Updated note
 */
export const updateSessionNote = (sessionId, noteId, data) => put(`/sessions/${sessionId}/notes/${noteId}`, data);

// =============================================================================
// Session Signatures
// =============================================================================

/**
 * Submit therapist signature
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Signature data
 * @param {string} data.signature - Base64 encoded signature
 * @returns {Promise} Signature result
 */
export const submitTherapistSignature = (sessionId, data) => post(`/sessions/${sessionId}/signatures/therapist`, data);

/**
 * Submit caregiver signature
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Signature data
 * @param {string} data.signature - Base64 encoded signature
 * @param {string} data.signer_name - Name of signer
 * @param {string} data.relationship - Relationship to client
 * @returns {Promise} Signature result
 */
export const submitCaregiverSignature = (sessionId, data) => post(`/sessions/${sessionId}/signatures/caregiver`, data);

export default {
  // Sessions
  listSessions,
  getSession,
  createSession,
  updateSession,
  deleteSession,
  // Status
  startSession,
  completeSession,
  cancelSession,
  markNoShow,
  // Reference Data
  getSessionReferenceData,
  // Scheduling
  listSchedulingTemplates,
  createSchedulingTemplate,
  createSchedulingInstance,
  // Therapist queries
  getTherapistDailySessions,
  getTherapistCalendar,
  getPendingDataEntry,
  // Data collection (v1 endpoints)
  getSessionData,
  getDataCollectionHistory,
  getSessionCollectors,
  getDataCollection,
  createDataCollection,
  addTrial,
  addBehavior,
  addAbc,
  addMand,
  addMaintenance,
  updateDataCollection,
  deleteDataCollection,
  // Notes
  getSessionNotes,
  addSessionNote,
  updateSessionNote,
  // Signatures
  submitTherapistSignature,
  submitCaregiverSignature
};
